import React from 'react';
import { Helmet } from 'react-helmet';
import SEOMetadata from '@/components/SEOMetadata';
import HeroSection from "@/components/HeroSection";
import CTASection from "@/components/CTASection";
import ExecutiveCoachingFAQ from "@/components/executive-coaching/ExecutiveCoachingFAQ";
import { webPageStructuredData } from '@/data/structuredData';

const ExecutiveCoaching = () => {
  return (
    <>
      <SEOMetadata
        metaTitle="Souverän führen unter Druck | Executive Coaching Bayern | Jörg Hörmann"
        metaDescription="Souverän entscheiden, wenn es darauf ankommt. Executive Coaching für Führungskräfte in Bayern und remote. Erstgespräch vereinbaren."
        title="Souverän führen unter Druck | Executive Coaching Bayern | Jörg Hörmann"
        description="Souverän entscheiden, wenn es darauf ankommt. Executive Coaching für Führungskräfte in Bayern und remote. Erstgespräch vereinbaren."
        imageUrl="/images/og-portrait.jpg"
        pageName="Executive Coaching"
        primaryLink="/executive-coaching"
        keywords="Executive Coaching Bayern, Executive Coaching Deutschland, Katalysator-Führung, C-Level Coaching München, Geschäftsführer Coaching Nürnberg, Leadership Development"
        canonicalUrl="https://joerg-hoermann.de/executive-coaching"
      />
      <Helmet>
        <script type="application/ld+json">
          {JSON.stringify(webPageStructuredData({
            name: "Executive Coaching für zukunftsfähige Führung",
            description: "Executive Coaching für Geschäftsführer & C-Level. Katalysator-Führung mit emotionaler Intelligenz. Bayern & deutschlandweit.",
            url: "https://joerg-hoermann.de/executive-coaching",
            aboutTopics: ["executiveCoaching", "katalysatorFuehrung", "emotionaleIntelligenz", "resilienz"]
          }))}
        </script>
      </Helmet>
      <main>
        <HeroSection
          title="Souverän führen, wenn es komplex wird."
          subtitle="Begleitung für Entscheider, die Klarheit suchen, nicht nur Methoden."
          description="Individuelle Begleitung für Führungskräfte, die unter Druck klar bleiben wollen. Die Katalysator-Methode verbindet emotionale Intelligenz mit strategischer Klarheit – für Entscheider in Bayern und deutschlandweit."
          primaryCTA={{ text: "Erstgespräch vereinbaren", link: "/kontakt" }}
          backgroundClass="bg-gradient-to-br from-joerg-50 to-white"
          imageUrl="/images/blog/business-meeting.jpg"
          hideHeroLogo={true}
        />


        {/* AI-Summary / AEO Abstract */}
        <section className="sr-only" aria-label="Zusammenfassung" data-speakable="summary">
          <p>Executive Coaching von Jörg Hörmann richtet sich an Führungskräfte aller Ebenen – von Teamleads über Abteilungsleiter bis zu C-Level Executives. Die Katalysator-Methode verbindet emotionale Intelligenz mit strategischer Exzellenz, fundiert durch Erkenntnisse der Neuroplastizität und Resilienzforschung. Verfügbar in Bayern und deutschlandweit.</p>
        </section>

        <section className="py-10 bg-white" data-speakable="definition">
          <div className="container mx-auto px-4">
            <p className="max-w-3xl mx-auto text-lg lg:text-xl text-joerg-700 leading-relaxed italic border-l-4 border-primary/30 pl-6">
              <strong className="not-italic">Was ist Executive Coaching?</strong>{" "}
              Executive Coaching ist die vertrauliche 1:1-Begleitung für Führungskräfte, die unter Druck handlungsfähig bleiben wollen. Die Katalysator-Methode schafft den Raum, in dem Entscheidungen entstehen, hinter denen man stehen kann, auch in unübersichtlichen Situationen.
            </p>
          </div>
        </section>

        <section className="section-spacing bg-joerg-50">
          <div className="container max-w-4xl">
            <h2 className="text-center mb-12">Was sich durch Coaching verändert</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white p-6 rounded-lg border border-joerg-200">
                <h3 className="text-xl font-semibold text-joerg-800 mb-4">Strategische Klarheit</h3>
                <p className="text-joerg-700">
                  Gewinnen Sie Klarheit über Ihre strategischen Ziele und entwickeln Sie Entscheidungskompetenz für komplexe Führungssituationen.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-joerg-200">
                <h3 className="text-xl font-semibold text-joerg-800 mb-4">Emotionale Intelligenz</h3>
                <p className="text-joerg-700">
                  Stärken Sie Ihre emotionale Intelligenz und lernen Sie, Teams nachhaltig zu inspirieren und zu befähigen.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-joerg-200">
                <h3 className="text-xl font-semibold text-joerg-800 mb-4">Führen in Veränderung</h3>
                <p className="text-joerg-700">
                  Veränderungsprozesse gelingen, wenn Führungskräfte selbst klar und stabil sind. Wir arbeiten an genau dieser inneren Basis.
                </p>
              </div>
              <div className="bg-white p-6 rounded-lg border border-joerg-200">
                <h3 className="text-xl font-semibold text-joerg-800 mb-4">Resilienz stärken</h3>
                <p className="text-joerg-700">
                  Entwickeln Sie innere Stabilität und Handlungsfähigkeit, auch unter Druck und in komplexen Führungssituationen.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="section-spacing bg-white">
          <div className="container max-w-4xl">
            <h2 className="text-center mb-12">Katalysator-Führung: Der Ansatz</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-joerg-700 leading-relaxed mb-6">
                Ein Katalysator beschleunigt Prozesse, ohne selbst verbraucht zu werden. Die <strong>Katalysator-Führung</strong> überträgt dieses Prinzip auf Führung: Sie schaffen die Bedingungen, in denen Ihr Team eigenständig wirksam wird.
              </p>
              <div className="bg-joerg-50 p-6 rounded-lg mb-6">
                <h3 className="text-xl font-semibold text-joerg-800 mb-4">Vier Dimensionen</h3>
                <ol className="space-y-3 text-joerg-700">
                  <li><strong>Emotionale Intelligenz:</strong> Verstehen und steuern Sie Emotionen - bei sich selbst und in Ihrem Team</li>
                  <li><strong>Strategische Exzellenz:</strong> Treffen Sie fundierte Entscheidungen mit Weitblick</li>
                  <li><strong>Systemisches Denken:</strong> Erkennen Sie Zusammenhänge und nutzen Sie Hebelpunkte</li>
                  <li><strong>Authentische Führung:</strong> Entwickeln Sie Ihren eigenen, authentischen Führungsstil</li>
                </ol>
              </div>
            </div>
          </div>
        </section>

        {/* Golden Sentence – emotionaler Abschluss */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <p className="max-w-2xl mx-auto text-center italic text-joerg-700 text-lg lg:text-xl leading-loose">
              „Die stärksten Führungskräfte, die ich begleite, gewinnen nicht durch Kontrolle. Sie gewinnen, weil sie einen Raum halten können, in dem ihr Team zur Bestform findet."
            </p>
          </div>
        </section>

        <ExecutiveCoachingFAQ />

        <CTASection
          title="Ein ruhiges Gespräch über Ihre nächsten Schritte"
          description="Lassen Sie uns unverbindlich herausfinden, ob und wie ich Sie begleiten kann."
          primaryCTA={{ text: "Erstgespräch vereinbaren", link: "/kontakt" }}
          backgroundClass="bg-gradient-to-br from-[#EDF5E9] to-[#E8F1FB] text-joerg-800"
        />
      </main>
    </>
  );
};

export default ExecutiveCoaching;
